import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  ClipboardList,
  Search,
  RefreshCcw,
  Printer,
  FileSpreadsheet,
  Info,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

const monthOptions = [
  { label: "Select", value: "Select" },
  { label: "January", value: "1" },
  { label: "February", value: "2" },
  { label: "March", value: "3" },
  { label: "April", value: "4" },
  { label: "May", value: "5" },
  { label: "June", value: "6" },
  { label: "July", value: "7" },
  { label: "August", value: "8" },
  { label: "September", value: "9" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

const DailyAttendanceReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchid") || localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchtype") || localStorage.getItem("branchType") || "HO";

  const [loading, setLoading] = useState(false);

  // meta
  const [branches, setBranches] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [years, setYears] = useState([]);
  const [statusOptions, setStatusOptions] = useState([{ label: "Select", value: "Select" }]);
const [metaReady, setMetaReady] = useState(false);
  // filters
  const [empType, setEmpType] = useState("working"); // working | resigned
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");
  const [designation, setDesignation] = useState("Select"); // store name (ASP passes SelectedItem.Text)
  const [branch, setBranch] = useState("Select");

  // IMPORTANT: input type="date" -> ISO yyyy-mm-dd
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [month, setMonth] = useState("Select");
  const [year, setYear] = useState("Select");
  const [status, setStatus] = useState("Select");

  // autocomplete
  const [empCodeSug, setEmpCodeSug] = useState([]);
  const [empNameSug, setEmpNameSug] = useState([]);
  const [showEmpCodeSug, setShowEmpCodeSug] = useState(false);
  const [showEmpNameSug, setShowEmpNameSug] = useState(false);

  // list + pagination
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-43", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const actionBtn =
    `inline-flex items-center justify-center gap-2 px-4 py-2 rounded-2xl text-white font-bold shadow ` +
    `transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const fetchMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(branchId).trim() || !String(branchType).trim()) {
      toast.warn("Branch info missing in localStorage (branchId/branchType).");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/daily-attendance-report/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType: branchType,
        },
      });

      if (res?.data?.success) {
        setBranches(res.data.branches || []);
        setDesignations(res.data.designations || []);
        setYears(res.data.years || []);
        setStatusOptions(res.data.statusOptions || [{ label: "Select", value: "Select" }]);

        // defaults like ASP
        const defMonth = String(res.data.defaultMonth || "");
        const defYear = String(res.data.defaultYear || "");
        const defBranch = String(res.data.defaultBranchId || "");

        setMonth(defMonth || "Select");
        setYear(defYear || "Select");
        setBranch(defBranch || "Select");
        setStatus("Select");
        setDesignation("Select");

        setMetaReady(true);
      } else {
        toast.error(res?.data?.message || "Failed to load meta.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta error");
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (p = page) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);

      const resign = empType === "resigned" ? 0 : 1;

      const res = await axios.get(`${BASE_URL}/api/daily-attendance-report/data`, {
        params: {
          finYearId: Number(finYearId),
          branchType: branchType,
          branchId: branch,
          empcode: empCode.trim(),
          empname: empName.trim(),
          designation: designation === "Select" ? "" : designation,
          fromDate: fromDate || "",
          toDate: toDate || "",
          month: month,
          year: year,
          resign,
          status: status === "Select" ? "" : status,
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load report.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Report load error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  fetchMeta();
}, []);

// initial fetch after meta is ready
useEffect(() => {
  if (!metaReady) return;
  fetchList(page);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [metaReady, page]);

// fetch when page changes (only after meta ready)
useEffect(() => {
  if (!metaReady) return;
  fetchList(page);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [page, metaReady]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleViewAll = async () => {
    setEmpType("working");
    setEmpCode("");
    setEmpName("");
    setDesignation("Select");
    setFromDate("");
    setToDate("");
    // keep branch as default selected branch like ASP
    setStatus("Select");

    setPage(1);
    await fetchList(1);
  };

  const handleDetails = () => {
    // ASP validation: if resigned + empcode empty => alert
    if (empType === "resigned" && !empCode.trim()) {
      toast.warn("Enter Employee Code");
      return;
    }

    // open new tab (you’ll map this route later)
    const qs = new URLSearchParams({
      empType,
      branch,
      empcode: empCode.trim(),
      empname: empName.trim(),
      designation: designation === "Select" ? "" : designation,
      fromDate: fromDate || "",
      toDate: toDate || "",
      month,
      year,
      status: status === "Select" ? "" : status,
    }).toString();

    window.open(`/PayRoll/DailyAttendanceDetails?${qs}`, "_blank");
  };

  const handlePrint = () => {
    const qs = new URLSearchParams({
  finYearId,
  branchType,
  empType,
  branch,
  empcode: empCode.trim(),
  empname: empName.trim(),
  designation: designation === "Select" ? "" : designation,
  fromDate,
  toDate,
  month,
  year,
  status: status === "Select" ? "" : status,
}).toString();

    window.open(`/PayRoll/DailyAttendanceReportPrint?${qs}`, "_blank");
  };

const handleExcel = () => {
  const resign = empType === "resigned" ? 0 : 1;

  const qs = new URLSearchParams({
    finYearId,
    branchType,
    branchId: branch,
    empcode: empCode.trim(),
    empname: empName.trim(),
    designation: designation === "Select" ? "" : designation,
    fromDate,
    toDate,
    month,
    year,
    resign,
    status: status === "Select" ? "" : status,
  }).toString();

  window.open(`${BASE_URL}/api/daily-attendance-report/excel?${qs}`, "_blank");
};

  // Autocomplete (simple debounce)
  useEffect(() => {
    let t = setTimeout(async () => {
      try {
        if (!empCode.trim() || empCode.trim().length < 1) {
          setEmpCodeSug([]);
          return;
        }
        const res = await axios.get(`${BASE_URL}/api/daily-attendance-report/empcode-autocomplete`, {
          params: {
            finYearId: Number(finYearId),
            branchId: Number(branchId),
            branchType,
            q: empCode.trim(),
          },
        });
        if (res?.data?.success) {
          setEmpCodeSug(res.data.items || []);
        }
      } catch {
        // silent
      }
    }, 250);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empCode]);

  useEffect(() => {
    let t = setTimeout(async () => {
      try {
        if (!empName.trim() || empName.trim().length < 1) {
          setEmpNameSug([]);
          return;
        }
        const res = await axios.get(`${BASE_URL}/api/daily-attendance-report/empname-autocomplete`, {
          params: {
            finYearId: Number(finYearId),
            branchId: Number(branchId),
            branchType,
            q: empName.trim(),
          },
        });
        if (res?.data?.success) {
          setEmpNameSug(res.data.items || []);
        }
      } catch {
        // silent
      }
    }, 250);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empName]);

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

const openMCL = async () => {
  if (month === "Select" || year === "Select") {
    toast.warn("Select Month and Year");
    return;
  }
  window.open(`/PayRoll/ManagementLeaveCalculationView?month=${month}&year=${year}`, "_blank");
};

  const openLeaveLetter = (row) => {
    const employeeId = row?.fld_employeeid || row?.fld_Employeeid;
    if (!employeeId) return toast.error("Employee ID missing");
    if (month === "Select" || year === "Select") {
      toast.warn("Select Month and Year");
      return;
    }
    window.open(`/PayRoll/LeaveLetter?employeeId=${employeeId}&month=${month}&year=${year}`, "_blank");
  };

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">DAILY ATTENDANCE REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <ClipboardList className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Daily Attendance Report
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search / View All / Details / Print / Pagination
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleViewAll} className={ghostBtn} disabled={loading}>
                <RefreshCcw className="w-4 h-4" />
                View All
              </button>

              <button onClick={handleDetails} className={ghostBtn}>
                <Info className="w-4 h-4" />
                Details
              </button>

              <button onClick={handlePrint} className={ghostBtn}>
                <Printer className="w-4 h-4" />
                Print
              </button>

              <button onClick={handleExcel} className={primaryBtn} disabled={loading}>
                <FileSpreadsheet className="w-4 h-4" />
                Excel
              </button>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            {/* Working/Resigned */}
            <div className="flex flex-wrap gap-6 mb-6">
              <label className="flex items-center gap-2 font-bold">
                <input
                  type="radio"
                  name="empType"
                  checked={empType === "working"}
                  onChange={() => setEmpType("working")}
                />
                Working Employees
              </label>
              <label className="flex items-center gap-2 font-bold">
                <input
                  type="radio"
                  name="empType"
                  checked={empType === "resigned"}
                  onChange={() => setEmpType("resigned")}
                />
                Resigned Employees
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Emp Name */}
              <div className="relative">
                <label className={labelBase}>Employee Name</label>
                <input
                  value={empName}
                  onChange={(e) => {
                    setEmpName(e.target.value);
                    setShowEmpNameSug(true);
                  }}
                  onBlur={() => setTimeout(() => setShowEmpNameSug(false), 150)}
                  placeholder="Type employee name..."
                  className={`${inputBase} mt-2`}
                />
                {showEmpNameSug && empNameSug.length > 0 && (
                  <div className={`absolute z-20 mt-1 w-full rounded-2xl border shadow-lg max-h-52 overflow-auto ${
                    isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"
                  }`}>
                    {empNameSug.slice(0, 10).map((x, i) => (
                      <button
                        type="button"
                        key={i}
                        className={`w-full text-left px-4 py-2 hover:${isDark ? "bg-white/10" : "bg-gray-50"}`}
                        onMouseDown={() => {
                          setEmpName(x);
                          setShowEmpNameSug(false);
                        }}
                      >
                        {x}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Emp Code */}
              <div className="relative">
                <label className={labelBase}>Employee Code</label>
                <input
                  value={empCode}
                  onChange={(e) => {
                    setEmpCode(e.target.value);
                    setShowEmpCodeSug(true);
                  }}
                  onBlur={() => setTimeout(() => setShowEmpCodeSug(false), 150)}
                  placeholder="Type employee code..."
                  className={`${inputBase} mt-2`}
                />
                {showEmpCodeSug && empCodeSug.length > 0 && (
                  <div className={`absolute z-20 mt-1 w-full rounded-2xl border shadow-lg max-h-52 overflow-auto ${
                    isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"
                  }`}>
                    {empCodeSug.slice(0, 10).map((x, i) => (
                      <button
                        type="button"
                        key={i}
                        className={`w-full text-left px-4 py-2 hover:${isDark ? "bg-white/10" : "bg-gray-50"}`}
                        onMouseDown={() => {
                          setEmpCode(x);
                          setShowEmpCodeSug(false);
                        }}
                      >
                        {x}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Designation */}
              <div>
                <label className={labelBase}>Designation</label>
                <select
                  value={designation}
                  onChange={(e) => setDesignation(e.target.value)}
                  className={`${inputBase} mt-2`}
                >
                  <option value="Select">Select</option>
                  {designations.map((d) => (
                    <option key={d.fld_desigid} value={d.fld_designationname}>
                      {d.fld_designationname}
                    </option>
                  ))}
                </select>
              </div>

              {/* Branch */}
              <div>
                <label className={labelBase}>Branch</label>
                <select value={branch} onChange={(e) => setBranch(e.target.value)} className={`${inputBase} mt-2`}>
  <option value="Select">Select</option>
  {branches.map((b) => (
    <option key={b.fld_branchid} value={String(b.fld_branchid)}>
      {b.fld_branchname}
    </option>
  ))}
</select>
              </div>

              {/* From Date */}
              <div>
                <label className={labelBase}>From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className={`${inputBase} mt-2`}
                />
              </div>

              {/* To Date */}
              <div>
                <label className={labelBase}>To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className={`${inputBase} mt-2`}
                />
              </div>

              {/* Year */}
              <div>
                <label className={labelBase}>Year</label>
                <select value={year} onChange={(e) => setYear(e.target.value)} className={`${inputBase} mt-2`}>
  <option value="Select">Select</option>
  {years.map((y) => (
    <option key={y} value={String(y)}>
      {y}
    </option>
  ))}
</select>
              </div>

              {/* Month */}
              <div>
                <label className={labelBase}>Month</label>
                <select value={month} onChange={(e) => setMonth(e.target.value)} className={`${inputBase} mt-2`}>
  {monthOptions.map((m) => (
    <option key={m.value} value={String(m.value)}>
      {m.label}
    </option>
  ))}
</select>
              </div>

              {/* Status */}
              <div>
                <label className={labelBase}>Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputBase} mt-2`}>
                  {statusOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-3 mt-6">
              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>
                    <th className="px-4 py-4 text-center font-bold">Gross Salary</th>
                    <th className="px-4 py-4 text-center font-bold">Acc.No</th>
                    <th className="px-4 py-4 text-center font-bold">Opening Leave Balance</th>
                    <th className="px-4 py-4 text-center font-bold">Open Leave Transactions</th>
                    <th className="px-4 py-4 text-center font-bold">Closing Leave Balance</th>
                    <th className="px-4 py-4 text-center font-bold">Monthly CL</th>
                    <th className="px-4 py-4 text-center font-bold">Days Worked</th>
                    <th className="px-4 py-4 text-center font-bold">Camp</th>
                    <th className="px-4 py-4 text-center font-bold">Leave Taken</th>
                    <th className="px-4 py-4 text-center font-bold">Mng Leave 100%</th>
                    <th className="px-4 py-4 text-center font-bold">Mng Leave 30%</th>
                    <th className="px-4 py-4 text-center font-bold">Holidays</th>
                    <th className="px-4 py-4 text-center font-bold">LOP</th>
                    <th className="px-4 py-4 text-center font-bold">Compulsory LOP</th>
                    <th className="px-4 py-4 text-center font-bold">Total LOP</th>
                    <th className="px-4 py-4 text-center font-bold">Total Working Days</th>
                    <th className="px-4 py-4 text-center font-bold">Branch</th>
                    <th className="px-4 py-4 text-center font-bold">Leave Letter</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 22 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={22} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const lopVal = Number(r.fld_leavelop ?? r.fld_lop ?? 0) || 0;
                      const isRed = lopVal > 0;

                      return (
                        <tr
                          key={(r.fld_employeeid || r.fld_Employeeid || idx) + "-" + idx}
                          className={`${isRed ? "bg-red-600/25" : (isDark ? "bg-gray-900" : "bg-white")} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_Employeecode}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_Employee}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_designationname}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_grosssalary}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_accountno}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_totleaveob}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_openleavetransactions}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_closingleavebal}
                          </td>

                          <td className="px-4 py-4 text-sm text-center">
                            <button
                              type="button"
                              onClick={openMCL}
                              className={`underline font-bold ${isDark ? "text-blue-300" : "text-blue-700"}`}
                            >
                              {r.fld_monthlycausalleave}
                            </button>
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.Daysworked}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.camp}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.Leavetaken}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.manageleave100p}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.manageleave}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.Holidays}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center font-bold ${isRed ? "text-red-400" : (isDark ? "text-gray-300" : "text-gray-900")}`}>
                            {r.fld_leavelop}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_compulsorylop}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_lop}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.Workingdays}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_BranchName}
                          </td>
                          <td className="px-4 py-4 text-center">
                            <button type="button" onClick={() => openLeaveLetter(r)} className={actionBtn}>
                              Leave Letter
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {totalPages > 1 && (
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        page === i + 1
                          ? `${primaryBtnClass} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default DailyAttendanceReport;